import { useState } from "react";
import { useParams } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { useStatus } from "../hooks/useStatus";
import GlobalStatusBanner from "../components/GlobalStatusBanner";
import StatusBadge from "../components/StatusBadge";
import UptimeBars from "../components/UptimeBars";
import IncidentTimeline from "../components/IncidentTimeline";
import MaintenanceBanner from "../components/MaintenanceBanner";
import { buildApiUrl } from "../lib/api";
import type { DailyStats, Incident, IncidentUpdate } from "@statuspage/shared";

// Minimal runtime validation helpers (no zod dependency)
function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}

function safeArray<T>(value: unknown, guard: (item: unknown) => item is T): T[] {
  return Array.isArray(value) ? value.filter(guard) : [];
}

function isDailyStats(item: unknown): item is DailyStats {
  return isRecord(item) && typeof item.date === "string" && (item.uptime_pct === null || typeof item.uptime_pct === "number");
}

function isIncident(item: unknown): item is Incident {
  return isRecord(item) && typeof item.id === "string" && typeof item.title === "string";
}

function isIncidentUpdate(item: unknown): item is IncidentUpdate {
  return isRecord(item) && typeof item.id === "string" && typeof item.body === "string";
}

// Inline fetch for service history (UptimeBars data)
async function fetchServiceHistory(
  tenant: string,
  serviceId: string,
  days: 30 | 60 | 90
): Promise<DailyStats[]> {
  const res = await fetch(buildApiUrl(`/api/${tenant}/services/${serviceId}/history?days=${days}`));
  if (!res.ok) return [];
  const json: unknown = await res.json();
  if (!isRecord(json)) return [];
  const raw = json.daily_stats;
  return safeArray(raw, isDailyStats);
}

// Inline fetch for incident detail (timeline)
async function fetchIncidentDetail(
  tenant: string,
  incidentId: string
): Promise<{ incident: Incident; incident_updates: IncidentUpdate[] } | null> {
  const res = await fetch(buildApiUrl(`/api/${tenant}/incidents/${incidentId}`));
  if (!res.ok) return null;
  const json: unknown = await res.json();
  if (!isRecord(json)) return null;
  const incident = json.incident;
  const incident_updates = json.incident_updates;
  if (!isIncident(incident)) return null;
  return { incident, incident_updates: safeArray(incident_updates, isIncidentUpdate) };
}

export default function StatusPage() {
  const { tenant } = useParams<{ tenant: string }>();
  const { data, error, loading } = useStatus(tenant);

  // Per-service uptime bars state
  const [expandedService, setExpandedService] = useState<string | null>(null);
  const [uptimeDays, setUptimeDays] = useState<30 | 60 | 90>(30);
  const [uptimeStats, setUptimeStats] = useState<DailyStats[]>([]);
  const [uptimeLoading, setUptimeLoading] = useState(false);

  // Incident detail state
  const [expandedIncident, setExpandedIncident] = useState<string | null>(null);
  const [incidentDetail, setIncidentDetail] = useState<{
    incident: Incident;
    incident_updates: IncidentUpdate[];
  } | null>(null);
  const [incidentLoading, setIncidentLoading] = useState(false);

  const handleToggleUptime = async (serviceId: string) => {
    if (expandedService === serviceId) {
      setExpandedService(null);
      return;
    }
    if (!tenant) return;
    setExpandedService(serviceId);
    setUptimeLoading(true);
    const stats = await fetchServiceHistory(tenant, serviceId, uptimeDays);
    setUptimeStats(stats);
    setUptimeLoading(false);
  };

  const handleUptimeDaysChange = async (days: 30 | 60 | 90) => {
    setUptimeDays(days);
    if (!expandedService || !tenant) return;
    setUptimeLoading(true);
    const stats = await fetchServiceHistory(tenant, expandedService, days);
    setUptimeStats(stats);
    setUptimeLoading(false);
  };

  const handleToggleIncident = async (incidentId: string) => {
    if (expandedIncident === incidentId) {
      setExpandedIncident(null);
      setIncidentDetail(null);
      return;
    }
    if (!tenant) return;
    setExpandedIncident(incidentId);
    setIncidentLoading(true);
    const detail = await fetchIncidentDetail(tenant, incidentId);
    setIncidentDetail(detail);
    setIncidentLoading(false);
  };

  // --- Loading state ---
  if (loading) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-slate-50 dark:bg-slate-950">
        <div className="text-center">
          <div
            className="mx-auto h-8 w-8 animate-spin rounded-full border-4 border-slate-300 border-t-blue-600 dark:border-slate-700 dark:border-t-blue-400"
            role="status"
            aria-label="Lädt..."
          />
          <p className="mt-3 text-sm text-slate-600 dark:text-slate-300">Loading status...</p>
        </div>
      </div>
    );
  }

  // --- Error state ---
  if (error || !data) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-slate-50 px-4 py-8 dark:bg-slate-950">
        <div className="max-w-md text-center">
          <h1 className="text-2xl font-bold text-slate-900 dark:text-slate-100">Status Unavailable</h1>
          <p className="mt-2 text-sm text-slate-600 dark:text-slate-300">
            {error ?? "Could not load status data. Please try again later."}
          </p>
        </div>
      </div>
    );
  }

  const { tenant: tenantInfo, overall_status, services, active_incidents, upcoming_maintenance } = data;

  return (
    <>
      <Helmet>
        <title>{tenantInfo.name} — Status</title>
      </Helmet>

      <div className="min-h-screen bg-slate-50 dark:bg-slate-950">
        {/* Global Status Banner */}
        <GlobalStatusBanner
          overallStatus={overall_status}
          tenantName={tenantInfo.name}
          brandColor={tenantInfo.brand_color}
        />

        <main className="mx-auto max-w-3xl space-y-6 px-4 py-8 sm:px-6 lg:px-8">
          {/* Upcoming Maintenance */}
          {upcoming_maintenance.length > 0 && (
            <section aria-labelledby="maintenance-heading">
              <h2 id="maintenance-heading" className="mb-3 text-lg font-semibold text-slate-900 dark:text-slate-100">
                Scheduled Maintenance
              </h2>
              <div className="space-y-3">
                {upcoming_maintenance.map((m) => (
                  <MaintenanceBanner key={m.id} maintenance={m} />
                ))}
              </div>
            </section>
          )}

          {/* Services */}
            <section aria-labelledby="services-heading">
              <h2 id="services-heading" className="mb-3 text-lg font-semibold text-slate-900 dark:text-slate-100">
                Services
              </h2>
            <div className="space-y-2">
              {services.map((service) => (
                <div key={service.id}>
                  <button
                    onClick={() => handleToggleUptime(service.id)}
                    className="flex w-full items-center justify-between rounded-lg border border-slate-200 bg-white px-4 py-3 text-left shadow-sm transition-colors hover:bg-slate-50 focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 dark:border-slate-700 dark:bg-slate-900 dark:hover:bg-slate-800"
                    aria-expanded={expandedService === service.id}
                    aria-controls={`uptime-${service.id}`}
                  >
                    <div className="flex items-center gap-3">
                      <span className="text-sm font-medium text-slate-900 dark:text-slate-100">{service.name}</span>
                      {service.group_name && (
                        <span className="rounded bg-slate-100 px-1.5 py-0.5 text-xs text-slate-600 dark:bg-slate-800 dark:text-slate-300">
                          {service.group_name}
                        </span>
                      )}
                    </div>
                  <StatusBadge status={service.current_status} />
                  </button>

                  {/* Expanded uptime bars */}
                  {expandedService === service.id && (
                    <div
                      id={`uptime-${service.id}`}
                      className="rounded-b-lg border-x border-b border-slate-200 bg-white px-4 py-3 dark:border-slate-700 dark:bg-slate-900"
                    >
                      {uptimeLoading ? (
                        <p className="text-sm text-slate-500 dark:text-slate-400">Loading uptime data...</p>
                      ) : uptimeStats.length > 0 ? (
                        <UptimeBars
                          dailyStats={uptimeStats}
                          days={uptimeDays}
                          onDaysChange={handleUptimeDaysChange}
                        />
                      ) : (
                        <p className="text-sm text-slate-500 dark:text-slate-400">No uptime data available.</p>
                      )}
                    </div>
                  )}
                </div>
              ))}
            </div>
          </section>

          {/* Active Incidents */}
          {active_incidents.length > 0 && (
            <section aria-labelledby="incidents-heading">
              <h2 id="incidents-heading" className="mb-3 text-lg font-semibold text-slate-900 dark:text-slate-100">
                Active Incidents
              </h2>
              <div className="space-y-3">
                {active_incidents.map((incident) => (
                  <div key={incident.id}>
                    <button
                    onClick={() => handleToggleIncident(incident.id)}
                       className="flex w-full items-center justify-between rounded-lg border border-slate-200 bg-white px-4 py-3 text-left shadow-sm transition-colors hover:bg-slate-50 focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 dark:border-slate-700 dark:bg-slate-900 dark:hover:bg-slate-800"
                       aria-expanded={expandedIncident === incident.id}
                       aria-controls={`incident-${incident.id}`}
                     >
                       <div className="flex items-center gap-3">
                         <span className="text-sm font-medium text-slate-900 dark:text-slate-100">
                           {incident.title}
                         </span>
                         <span
                           className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium ${
                              incident.severity === "critical"
                                ? "bg-red-100 text-red-800 dark:bg-red-950 dark:text-red-200"
                                : incident.severity === "major"
                                  ? "bg-orange-100 text-orange-800 dark:bg-orange-950 dark:text-orange-200"
                                  : "bg-yellow-100 text-yellow-800 dark:bg-yellow-950 dark:text-yellow-200"
                           }`}
                         >
                           {incident.severity}
                         </span>
                       </div>
                       <span className="text-xs text-slate-500 dark:text-slate-300">{incident.status}</span>
                     </button>

                    {expandedIncident === incident.id && (
                    <div
                      id={`incident-${incident.id}`}
                       className="rounded-b-lg border-x border-b border-slate-200 bg-white px-4 py-3 dark:border-slate-700 dark:bg-slate-900"
                    >
                      {incidentLoading ? (
                        <p className="text-sm text-slate-500 dark:text-slate-400">Loading incident details...</p>
                      ) : incidentDetail ? (
                        <IncidentTimeline
                          incident={incidentDetail.incident}
                          updates={incidentDetail.incident_updates}
                        />
                      ) : (
                        <p className="text-sm text-slate-500 dark:text-slate-400">Could not load incident details.</p>
                      )}
                    </div>
                  )}
                </div>
                ))}
              </div>
            </section>
          )}

          {/* Footer */}
          <footer className="border-t border-slate-200 pt-4 text-center text-xs text-slate-400 dark:border-slate-700 dark:text-slate-500">
            Powered by StatusPage
          </footer>
        </main>
      </div>
    </>
  );
}
